import React from'react';
import { useNavigate } from'react-router-dom';
import { useRealtimeQuery } from'../hooks/useApi';
import api from'../api/client';
import RealTimeTable from'../components/tables/RealTimeTable';
import KPICard from'../components/charts/KPICard';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import PageLoader from'../components/PageLoader';
import { Activity, AlertTriangle, Clock, Gauge, ShieldX } from'lucide-react';
import { formatLatency } from'../utils/format';

const COLUMNS = [
 {
 key:'timestamp',
 label:'Time',
 width:'140px',
 render: (val) => {
 if (!val) return'—';
 const d = new Date(val);
 return d.toLocaleTimeString(undefined, { hour:'2-digit', minute:'2-digit', second:'2-digit' });
 },
 },
 { key:'OperationName', label:'Operation', width:'180px' },
 { key:'ApiName', label:'API', width:'140px' },
 { key:'SubscriptionId', label:'Subscription', width:'160px' },
 { key:'Model', label:'Model', width:'120px' },
 { key:'PromptTokens', label:'Prompt', width:'80px' },
 { key:'TotalTokens', label:'Total', width:'80px' },
 {
 key:'DurationMs',
 label:'Latency',
 width:'90px',
 render: (val) => formatLatency(val),
 },
 {
 key:'StatusCode',
 label:'Status',
 width:'70px',
 render: (val) => {
 const code = parseInt(val, 10);
 const color = code >= 500 ?'text-red-600' : code >= 400 ?'text-amber-600' :'text-emerald-600';
 return <span className={`font-mono font-medium ${color}`}>{val}</span>;
 },
 },
 { key:'ClientIP', label:'Client IP', width:'120px' },
];

export default function RealTimeMonitor() {
 const navigate = useNavigate();

 const { data: calls, isPending: callsPending } = useRealtimeQuery(
'realtime-calls',
 () => api.getRealtimeCalls(100),
 { refetchInterval: 5000 }
 );

 const { data: stats, isPending: statsPending } = useRealtimeQuery(
'realtime-stats',
 () => api.getRealtimeStats(),
 { refetchInterval: 5000 }
 );

 const isLoading = callsPending || statsPending;

 const handleRowClick = (row) => {
 if (!row?.OperationId) return;
 // Pass SubscriptionId when available so the inspector can also load the
 // surrounding call list for the same subscription, not just this one call.
 const params = new URLSearchParams({ operationId: row.OperationId });
 if (row.SubscriptionId) params.set('subscriptionId', row.SubscriptionId);
 navigate(`/conversations?${params.toString()}`);
 };

 return (
 <div className="space-y-6">
 <Breadcrumbs />
 <div>
 <h1 className="text-xl font-bold">Real-Time Monitor</h1>  <p className="text-sm text-gray-500 mt-0.5">
 Live API call feed — last 15 minutes, refreshing every 5 seconds
 </p>
 </div>

 {/* Live KPIs */}
 {isLoading ? (
 <PageLoader message="Connecting to live feed…" />
 ) : (
 <>
 <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
 <ErrorBoundary name="Calls KPI">
 <KPICard
 title="Calls (15m)"
 value={stats?.TotalCalls}
 format="number"
 icon={Activity}
 color="brand"
 help="Served calls in the last 15 minutes (routed to a backend). Blocked auth/rate-limit attempts are shown separately as Edge Rejections — same definition as the Executive Dashboard."
 />
 </ErrorBoundary>
 <ErrorBoundary name="Calls/Min KPI">
 <KPICard
 title="Calls/Min"
 value={stats?.CallsPerMinute ? Math.round(stats.CallsPerMinute) : 0}
 format="number"
 icon={Gauge}
 color="violet"
 help="Average served calls per minute over the last 15 minutes."
 />
 </ErrorBoundary>
 <ErrorBoundary name="Error Rate KPI">
 <KPICard
 title="Error Rate"
 value={stats?.ErrorRate}
 format="percent"
 icon={AlertTriangle}
 color={parseFloat(stats?.ErrorRate) > 5 ?'red' :'emerald'}
 help="Share of SERVED calls that failed (4xx/5xx after routing to a backend). Excludes auth/rate-limit rejections, which are counted as Edge Rejections."
 />
 </ErrorBoundary>
 <ErrorBoundary name="Edge Rejections KPI">
 <KPICard
 title="Edge Rejections"
 value={stats?.EdgeRejections}
 format="number"
 icon={ShieldX}
 color={stats?.EdgeRejections != null && Number(stats.EdgeRejections) > 0 ?'red' :'emerald'}
 help="Requests refused at the gateway before reaching a backend (401/403/429). High values usually mean a misconfigured client, not a platform outage."
 subtitle="Blocked at gateway (401/403/429)"
 />
 </ErrorBoundary>
 <ErrorBoundary name="Avg Latency KPI">
 <KPICard
 title="Avg Latency"
 value={stats?.AvgLatency}
 format="ms"
 icon={Clock}
 color="amber"
 help="Average end-to-end response time for served calls over the last 15 minutes."
 />
 </ErrorBoundary>
 </div>

 {/* Live Feed Table */}
 <RealTimeTable
 data={calls || []}
 columns={COLUMNS}
 maxRows={100}
 onRowClick={handleRowClick}
 />
 </>
 )}
 </div>
 );
}
